/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import SmartsInterestingFieldRecommender from './SmartsInterestingFieldRecommender';
import SmartsRelationshipStrengthService from './SmartsRelationshipStrengthService';
import Config from '../components/startingPoints/relationshipGraph/Config';
import smartsPredictRecommenderService from './SmartsPredictRecommenderService';
import StringResources from 'nls/StringResources';

class SmartsRecommenderWrapper {
	constructor(options) {
		this.dashboardApi = options.dashboardApi;
		this.services = options.services;
		this.smartsInterestingFieldRecommender = options.smartsInterestingFieldRecommender || new SmartsInterestingFieldRecommender({
			dashboardApi: options.dashboardApi,
			requestName: 'SmartsInterestingFieldRecommender'
		});
		this.smartsRelationshipStrengthService = options.smartsRelationshipStrengthService || new SmartsRelationshipStrengthService({
			dashboardApi: options.dashboardApi,
			requestName: 'SmartsRelationshipStrengthService'
		});
		this.smartsPredictRecommenderService = options.smartsPredictRecommenderService || new smartsPredictRecommenderService({
			dashboardApi: options.dashboardApi,
			requestName: 'SmartsPredictRecommenderService'
		});
		this.logger = options.logger || {
			debug: function () { },
			error: function () { }
		};
	}

	getModule(dataSourceId) {
		return this.dashboardApi.getFeature('dataSources.deprecated').getModule(dataSourceId);
	}

	getMetadataColumns(dataSourceId) {
		return this.getModule(dataSourceId)
			.then(module => module.getMetadataColumns((col) =>
				!col.isHidden() && (col.getType() === 'fact' || col.getType() === 'attribute')));
	}

	recommendInterestingFields(dataSourceId) {
		const fieldRecommender = this.smartsInterestingFieldRecommender;
		return this.getModule(dataSourceId)
			.then(module => fieldRecommender.recommendInterestingFields({
				module: module
			}));
	}

	getRelationshipStrength = (requestParams, context, cancel) => {
		//TODO: Recreate this after Explore Error Class is created
		const cancelError = {
			'id': 'RUN_REQUEST_IN_BACKGROUND',
			getUserStrings: () => {
				return {
					'title': StringResources.get('startingPoints_relationship_cancel_title'),
					'message': StringResources.get('startingPoints_relationship_cancel_description'),
					'suggestions': StringResources.get('startingPoints_relationship_cancel_suggestions')
				};
			}
		};
		cancel = cancel.catch(() => Promise.reject(cancelError));
		return Promise.race([this._getRelationshipStrength(requestParams, context), cancel]);
	};

	/*
	 * @param {object} payload
	 * 	{
	 * 		dataSourceId: xxx,
	 * 		targetId: xxx,
	 * 		maxInfluencerFields: xxx,
	 * 		showConcepts: xxx,
	 * 		transformOptions: {}
	 * 		context: {
	 * 			relationshipVM: obj
	 * 		}
	 * 	}
	 */
	_getRelationshipStrength(payload) {
		return this.getModule(payload.dataSourceId)
			.then(module => {
				const requestParams = {
					sourceModuleId: module.getAssetId(),
					sourceType: module.getSourceType(),
					targetFieldIDForExpression: payload.targetId,
					maxInfluencerFields: payload.maxInfluencerFields ? payload.maxInfluencerFields : Config.max_nodes_to_display,
					showConcepts: payload.showConcepts,
					// for debugger purpose (when set to true, Smarts will add the section "exploreSpec" that contains all influencer nodes that Smarts send to Predict before any filtering)
					includeSmartsExploreSpec: false,
					shapedModule: (!!module.getShapingId()), //indicates that there is shaping
					requestId: payload.requestId
				};
				this._addRequestParams(requestParams, payload);
				const tempSessionModuleJSON = module.getTemporarySessionModuleJSON();
				if (tempSessionModuleJSON) {
					requestParams.tempModule = JSON.stringify(tempSessionModuleJSON);
				}
				return this.smartsRelationshipStrengthService.getRelationshipStrength(requestParams, {
					moduleAPI: module,
					glassContext: this.services.biGlass.glassContext,
					model: payload.context.relationshipVM,
					transformOptions: payload.context.transformOptions,
					dataSourceId: payload.dataSourceId
				});
			});
	}

	_addRequestParams(requestParams, payload) {
		if (payload && payload.context && payload.context.transformOptions && payload.context.transformOptions.lastAddedField) {
			requestParams = { ...requestParams, ...payload.context.transformOptions };
		}
	}

	recommendRelatedVisualizationsAsWidgetSpecs(dataSourceId, columnIds = [], options = {}) {
		const smartsRecommender = this.dashboardApi.getFeature('SmartsRecommender.deprecated');
		const visDefinitions = this.dashboardApi.getFeature('VisDefinitions');
		return smartsRecommender.recommendRelatedVisualizationsAsWidgetSpecs(dataSourceId, columnIds, !!options.includeBestVisForTargetFields)
			.then(items => {
				return items
					.map(item => {
						return {
							widgets: [
								{
									spec: item.spec,
									visDefinition: visDefinitions.getById(item.spec.visId)
								}
							],
							name: item.name
						};
					});
			});
	}

	getAlternativeVisualizations(widget) {
		if (!this._hasDataItems(widget)) {
			return Promise.resolve([]);
		}

		const smartVisRecommender = this._getSmartVisRecommender(widget);
		if (!smartVisRecommender || !smartVisRecommender.recommendAlternateVisualizationsAsWidgetSpecs) {
			return Promise.resolve([]);
		}

		return smartVisRecommender.recommendAlternateVisualizationsAsWidgetSpecs()
			.then(recommendations => {
				const visDefinitions = this.dashboardApi.getFeature('VisDefinitions');
				// alternative vis means everything except the current select widget vis type
				// TODO: ideally we would send an arg to the recommender to tell what charts to include and exclude so we don't have to do that
				return recommendations
					.filter(item => item.spec.visId !== widget.getVisId())
					.map(item => {
						// enhance the alternative recommendations response with more data needed by the view
						return {
							widgets: [
								{
									spec: item.spec,
									visDefinition: visDefinitions.getById(item.spec.visId)
								}
							],
							name: item.name
						};
					});
			});
	}

	getRelatedVisualizations(widget) {
		if (!this._hasDataItems(widget)) {
			return Promise.resolve([]);
		}

		const smartVisRecommender = this._getSmartVisRecommender(widget);
		if (!smartVisRecommender || !smartVisRecommender.recommendRelatedVisualizationsAsWidgetSpecs) {
			return Promise.resolve([]);
		}

		return smartVisRecommender.recommendRelatedVisualizationsAsWidgetSpecs()
			.then(recommendations => {
				const visDefinitions = this.dashboardApi.getFeature('VisDefinitions');
				return recommendations
					.map(item => {
						// enhance the recommended response with more data needed by the view
						return {
							widgets: [
								{
									spec: item.spec,
									visDefinition: visDefinitions.getById(item.spec.visId)
								}
							],
							name: item.name
						};
					});
			});
	}

	_getSmartVisRecommender(widget) {
		return this._getWidgetContent(widget).getFeature('Visualization.SmartsRecommender.deprecated');
	}

	_hasDataItems(widget){
		if(widget){
			const vis = this._getWidgetContent(widget).getFeature('Visualization');
			if(vis){
				const slots = vis.getSlots();
				const itemList = slots && slots.getDataItemList();
				return Array.isArray(itemList) && itemList.length > 0;
			}
			return false;
		}
		return false;
	}

	_getWidgetContent(widget) {
		return this.dashboardApi.getCanvas().getContent(widget.getId());
	}


	fetchRecommendations(widget, type) {
		const fetch = {
			'alternative': this.getAlternativeVisualizations.bind(this),
			'related': this.getRelatedVisualizations.bind(this)
		}[type];

		if (fetch) {
			return fetch(widget);
		} else {
			return Promise.resolve();
		}
	}

	fetchPredictRecommendations(dataSourceId, targetId) {
		return this.getModule(dataSourceId)
			.then(module => this.smartsPredictRecommenderService.fetchPredictRecommendations({
				moduleAPI: module,
				targetId: targetId
			}));
	}
}

export default SmartsRecommenderWrapper;



// WEBPACK FOOTER //
// ./src/js/util/SmartsRecommenderWrapper.js